import React, { useCallback, useMemo } from 'react';

import type { Question, ResponsePayload } from '../../../types/index';
import type { SubmitQuestionOptions } from '../useQuestionFlowManager';
import { getCustomScreenComponent } from './index';
import type { CustomScreenComponentProps } from './types';

interface CustomScreenQuestionProps {
  question: Question;
  response?: ResponsePayload;
  isSubmitting: boolean;
  onSubmit: (payload: ResponsePayload, options?: SubmitQuestionOptions) => void | Promise<void>;
}

export const CustomScreenQuestion: React.FC<CustomScreenQuestionProps> = ({
  question,
  response,
  isSubmitting,
  onSubmit,
}) => {
  const metadata = useMemo<Record<string, unknown>>(() => {
    if (question?.metadata && typeof question.metadata === 'object') {
      return { ...(question.metadata as Record<string, unknown>) };
    }
    return {};
  }, [question?.metadata]);

  const rawScreen =
    (metadata?.['screen'] as string | undefined) ??
    (metadata?.['screen_type'] as string | undefined);
  const screenName =
    typeof rawScreen === 'string' && rawScreen.trim().length > 0
      ? rawScreen.trim()
      : question?.key ?? undefined;

  const ScreenComponent = getCustomScreenComponent(screenName);

  const handleComplete = useCallback(
    (payload?: Partial<ResponsePayload>) => {
      if (!question?.id) {
        return;
      }

      const shouldComplete = payload?.is_completed ?? true;

      const basePayload: ResponsePayload = {
        question: question.id,
        is_completed: shouldComplete,
        ...payload,
      };

      onSubmit(basePayload, { advance: shouldComplete, persistImmediately: true });
    },
    [onSubmit, question?.id],
  );

  if (!ScreenComponent || !question?.id) {
    return (
      <div className="text-sm text-muted-foreground">
        This screen is not available right now. Please contact support for assistance.
      </div>
    );
  }

  const componentProps: CustomScreenComponentProps = {
    question,
    metadata,
    response,
    isSubmitting,
    onComplete: handleComplete,
  };

  return <ScreenComponent {...componentProps} />;
};
